import { getExtension } from '@/utils/file-type.utils';
import { IMAGE_EXTS, OFFICE_EXTS, TEXT_EXTS } from '@/constant/extensionTypes';

export type PreviewKind = 'pdf' | 'image' | 'text' | 'office' | 'unsupported';

/**
 * What a browser can draw for a file, from its name and recorded type.
 *
 * The mime wins where there is one: it is what the service validated and
 * stored, while the extension is only what somebody happened to name the file.
 */
export const detectPreviewKind = (
  name: string,
  mimeType?: string | null,
): PreviewKind => {
  const ext = getExtension(name) ?? '';
  const mime = (mimeType ?? '').toLowerCase();

  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || IMAGE_EXTS.includes(ext)) return 'image';
  if (mime.startsWith('text/') || TEXT_EXTS.includes(ext)) return 'text';
  if (OFFICE_EXTS.includes(ext)) return 'office';
  return 'unsupported';
};

/**
 * The same question, answered for a viewer rather than for a classifier:
 * anything unrecognised is drawn as a PDF instead of refused.
 *
 * ⚠️ This is the difference between "not a type I know" and "a type I know I
 * cannot draw", and the two must not share an answer. Tax names its documents
 * things like `Rental Statement - 2025.08.01 to 2025.08.31`, and when the
 * stored name has lost its extension `getExtension` reads the tail of that date
 * and reports `31`. A classifier is right to call that unsupported. A viewer
 * that does is showing a download button for a PDF it could have rendered -
 * which is exactly what every screen here did before it could branch at all.
 *
 * Office formats still get the download: `docx` is recognised, and recognised
 * is what earns a refusal.
 */
export const viewerPreviewKind = (
  fileName?: string | null,
  contentType?: string | null,
): PreviewKind => {
  const kind = detectPreviewKind(fileName ?? '', contentType);
  return kind === 'unsupported' ? 'pdf' : kind;
};

/** Read-URL query string that fits the page to the frame and keeps the toolbar. */
export const PDF_VIEW_HASH = '#view=FitH&navpanes=0&toolbar=1';

/** The ground every PDF viewer paints behind the page. */
export const VIEWER_BACKDROP = '#525659';

/**
 * Save a file from a signed URL.
 *
 * `window.document` rather than `document`: several callers name their own prop
 * `document`, and the shadowed global is a trap this module should not set.
 */
export const downloadFromUrl = (url: string, fileName?: string) => {
  const link = window.document.createElement('a');
  link.href = url;
  link.download = fileName || 'document';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  window.document.body.appendChild(link);
  link.click();
  link.remove();
};
